import type { StudyAnalysisResult } from './studyAnalysisApi';
import type { Point } from './StudyNotebookKnowledgePanel';

/** Fictional pre-authored data, never a learner receipt; distinct basename avoids TSX resolution collisions. */
export const MATERIAL_EXAMPLE_ID = 'example:material:equal-parts:v1';
export const exampleMaterial = {
  title: 'Fractions: from equal parts to quantities',
  pageTitles: ['One whole, equal parts', 'Equivalent fractions', 'A fraction of a quantity'],
};
export const exampleSource = [
  {
    id: 'b1',
    page_index: 0,
    text: 'A fraction names equal parts of the same whole. The denominator tells how many equal parts make the whole.',
  },
  {
    id: 'b2',
    page_index: 0,
    text: 'The numerator tells how many parts are being counted. One half and two quarters cover the same amount of one whole.',
  },
  {
    id: 'b3',
    page_index: 0,
    text: 'A pizza is cut into 8 equal slices. How many slices are three quarters of the pizza? Explain or draw your thinking.',
  },
  {
    id: 'b4',
    page_index: 1,
    text: 'Equivalent fractions describe the same amount of the same whole. Multiply or divide both the numerator and denominator by the same non-zero number to keep the value unchanged.',
  },
  {
    id: 'b5',
    page_index: 1,
    text: 'Worked example: split each third of one strip into two equal sections. Two thirds becomes four sixths: 2/3 = (2 × 2)/(3 × 2) = 4/6. The shaded length has not changed. Adding one to each number instead gives 3/4, which is not equivalent to 2/3.',
  },
  {
    id: 'b6',
    page_index: 2,
    text: 'To find a/b of a quantity N, find one of the b equal groups first, then take a groups: N ÷ b × a. Keep the unit and check that the result is sensible.',
  },
  {
    id: 'b7',
    page_index: 2,
    text: 'Worked example: find 3/5 of 20 counters. One fifth is 20 ÷ 5 = 4 counters. Three fifths is 3 × 4 = 12 counters. Twelve is less than twenty, as expected for a fraction less than one. Return to the pizza task on page 1: draw four equal groups, label one quarter, then explain how to find three quarters.',
  },
];
export const exampleNotes: NonNullable<StudyAnalysisResult['learning_notes']> = {
  depth: 'in_depth',
  chapters: [
    {
      title: 'Start with one whole and equal parts',
      body: 'A fraction only makes sense when we know the whole. Imagine one pizza. Equal parts must have the same size: eight equal slices are eight parts of this one pizza. If the pieces have different sizes, counting the pieces alone cannot tell us the fraction.\nBefore calculating, identify the whole and check the parts are equal. A fraction of one small pizza and the same fraction of a larger pizza need not be the same quantity.',
      block_ids: ['p0:b1'],
    },
    {
      title: 'Read the fraction and recognise equivalent amounts',
      body: 'In \\(3/4\\), 4 names the number of equal groups in the whole; 3 counts the groups wanted. The denominator need not equal the number of small slices: several slices can make one group.\nEquivalent fractions keep the same amount. Splitting every third into two changes two thirds to four sixths. Multiply both numbers by the same non-zero number; adding one to each is a different operation. Use the strip on page 2 to check the rule.',
      block_ids: ['p0:b2', 'p1:b4', 'p1:b5'],
    },
    {
      title: 'Find one group before finding several',
      body: 'For a/b of N, divide N into b equal groups, then count a groups. In the source example, 20 ÷ 5 gives 4 counters in one fifth; three groups give 12 counters. Include the unit in your answer.\nNow try the pizza task yourself. Draw eight slices as four equal groups and label one quarter before taking three quarters. Check against the whole pizza. The practice questions then change the quantity to test whether the method transfers.',
      block_ids: ['p2:b6', 'p2:b7', 'p0:b3'],
    },
  ],
};
const point = (id: string, name: string, explanation: string, source: number): Point => ({
  id: `${MATERIAL_EXAMPLE_ID}:${id}`,
  version: 1,
  source_version: 1,
  stale: false,
  state: 'not_started',
  mastery_state: 'not_assessed',
  practice_attempts: 0,
  review_links: [],
  content: {
    name,
    explanation,
    misconceptions: [],
    formulas: [],
    examples: [],
    question_ids: [],
    citations: [
      {
        block_id: `p${exampleSource[source].page_index}:${exampleSource[source].id}`,
        page_index: exampleSource[source].page_index,
        page_number: exampleSource[source].page_index + 1,
        quote: exampleSource[source].text,
      },
    ],
    figures: [],
    relations: [],
  },
});
export const examplePoints: Point[] = [
  point(
    'equal-parts',
    'Equal parts of one whole',
    'First identify one whole. Parts must be equal in size before we can count them as a fraction. The same amount can have different fraction names: one half and two quarters cover the same part of the same whole.',
    0,
  ),
  point(
    'fraction-numbers',
    'Read the numerator and denominator',
    'The bottom number names the total number of equal groups. The top number counts how many groups we want. Say the fraction in words before you calculate.',
    1,
  ),
  point(
    'quantity',
    'A fraction of a quantity',
    'The denominator tells you how many equal groups to make. Divide the quantity by that number to find one group; the numerator tells you how many groups to take. Keep the unit and check the result against the whole. The source counters example and the original pizza task use the same reasoning.',
    5,
  ),
];
examplePoints[0].content.examples = [
  {
    text: exampleSource[1].text,
    origin: 'source',
  },
];
examplePoints[0].content.misconceptions = [
  'Two unequal pieces are not automatically two halves.',
  'Fractions can only be compared directly when they refer to the same whole.',
];
examplePoints[1].content.formulas = [
  {
    latex: '\\frac{1}{2}=\\frac{2}{4}',
    explanation: 'Both names describe the same amount of one whole.',
    origin: 'source',
  },
];
examplePoints[1].content.name = 'Read fractions and find equivalent values';
examplePoints[1].content.explanation +=
  ' Equivalent fractions change both numbers by the same non-zero multiplier or divisor. Splitting each third into two produces four sixths from two thirds, without changing the amount.';
examplePoints[1].content.examples = [{ text: exampleSource[4].text, origin: 'source' }];
examplePoints[1].content.misconceptions = [
  'Adding one to the top and bottom does not usually preserve a fraction.',
  'Changing only the denominator changes the amount.',
];
examplePoints[1].content.citations.push({
  block_id: 'p1:b5',
  page_index: 1,
  page_number: 2,
  quote: exampleSource[4].text,
});
examplePoints[2].content.examples = [{ text: exampleSource[6].text, origin: 'source' }];
examplePoints[2].content.formulas = [
  {
    latex: '\\frac{a}{b}\\text{ of }N=(N\\div b)\\times a',
    explanation: 'Find one equal group before counting several.',
    origin: 'source',
  },
];
examplePoints[2].content.misconceptions = [
  'Dividing by the numerator makes the wrong number of groups.',
  'A fraction below one of a positive quantity should be less than the whole.',
];
export const examplePractice = [
  {
    id: `${MATERIAL_EXAMPLE_ID}:practice:equal`,
    knowledge_id: examplePoints[0].id,
    stem: 'A cake has 8 equal pieces. You take half of the whole cake. How many pieces do you take?',
    options: ['2 pieces', '4 pieces', '6 pieces', '8 pieces'],
    correct: 'B',
    hint: 'Make two equal groups from the eight pieces. Count one group.',
    feedback:
      'Split the eight equal pieces into two equal groups. Each group has four pieces. Four of the eight is one half.',
  },
  {
    id: `${MATERIAL_EXAMPLE_ID}:practice:meaning`,
    knowledge_id: examplePoints[1].id,
    stem: 'What does the 4 mean in 3/4?',
    options: [
      'The number of equal groups in the whole',
      'The number of groups counted',
      'The size of the pizza',
      'The number of pizzas',
    ],
    correct: 'A',
    hint: 'The bottom number describes how the whole is divided.',
    feedback:
      'The denominator 4 names four equal groups in one whole. The numerator 3 counts three of those groups.',
  },
  {
    id: `${MATERIAL_EXAMPLE_ID}:practice:pizza`,
    knowledge_id: examplePoints[2].id,
    stem: 'Return to page 1: the pizza has 8 equal slices. How many slices are three quarters?',
    options: ['2 slices', '3 slices', '6 slices', '8 slices'],
    correct: 'C',
    hint: 'Divide eight slices into four equal groups. Count three groups.',
    feedback:
      'One quarter is 8 ÷ 4 = 2 slices. Three quarters is 3 × 2 = 6 slices. Include the unit and check it is less than the whole.',
  },
  {
    id: `${MATERIAL_EXAMPLE_ID}:practice:transfer`,
    knowledge_id: examplePoints[2].id,
    stem: 'A different pizza has 12 equal slices. How many slices are three quarters of this pizza?',
    options: ['3 slices', '4 slices', '8 slices', '9 slices'],
    correct: 'D',
    hint: 'The fraction is unchanged, but the whole is now twelve slices. Find one quarter again.',
    feedback:
      'One quarter is 12 ÷ 4 = 3 slices. Three groups give 9 slices. The same fraction of a larger whole can be a larger quantity.',
  },
];
